import Papa from 'papaparse';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ROWS = 1000;
const MAX_FILE_SIZE_MB = 5;

/**
 * Validador CSV para invitaciones masivas al link de un plan.
 * Columnas: nombre, email
 */
export class PlanInviteCsvValidator {
  constructor() {
    this.requiredFields = ['nombre', 'email'];
  }

  normalizeHeader(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  async validateFile(file) {
    if (!file) {
      return { ok: false, error: 'Seleccioná un archivo CSV.' };
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return { ok: false, error: `El archivo no puede superar ${MAX_FILE_SIZE_MB} MB.` };
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      return { ok: false, error: 'El archivo debe ser en formato CSV.' };
    }

    return new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          resolve(this.buildValidationResult(results));
        },
        error: (error) => {
          resolve({ ok: false, error: `Error parseando CSV: ${error.message}` });
        },
      });
    });
  }

  buildValidationResult(results) {
    const fields = (results.meta?.fields || []).map((f) => this.normalizeHeader(f));
    const missing = this.requiredFields.filter((required) => !fields.includes(required));
    if (missing.length > 0) {
      return {
        ok: false,
        error: `Faltan columnas requeridas: ${missing.join(', ')}. Usá la plantilla descargada.`,
      };
    }

    const rows = results.data || [];
    if (rows.length === 0) {
      return { ok: false, error: 'El archivo no tiene filas de contactos.' };
    }
    if (rows.length > MAX_ROWS) {
      return { ok: false, error: `El máximo de contactos por archivo es ${MAX_ROWS}.` };
    }

    const nameKey = (results.meta.fields || []).find(
      (f) => this.normalizeHeader(f) === 'nombre',
    );
    const emailKey = (results.meta.fields || []).find(
      (f) => this.normalizeHeader(f) === 'email',
    );

    const validContacts = [];
    const invalidRows = [];
    const seenEmails = new Set();

    rows.forEach((row, index) => {
      const rowNumber = index + 2; // header = 1
      const name = String(row[nameKey] || '').trim();
      const email = String(row[emailKey] || '').trim().toLowerCase();

      if (!email) {
        invalidRows.push({ row: rowNumber, email: '', reason: 'Email vacío' });
        return;
      }

      if (!EMAIL_REGEX.test(email)) {
        invalidRows.push({ row: rowNumber, email, reason: 'Email inválido' });
        return;
      }

      if (seenEmails.has(email)) {
        invalidRows.push({ row: rowNumber, email, reason: 'Email duplicado' });
        return;
      }

      seenEmails.add(email);
      validContacts.push({
        name: name || null,
        email,
      });
    });

    if (validContacts.length === 0) {
      return {
        ok: false,
        error: 'No hay filas válidas para enviar.',
        invalidRows,
      };
    }

    return {
      ok: true,
      validContacts,
      invalidRows,
      totalRows: rows.length,
    };
  }
}
